/**
 * My Students State
 *
 * Manages the current user's students with real-time Firestore sync.
 * Automatically watches AccountState.studentIds() and loads fresh data whenever it changes.
 *
 * @example
 * // State auto-initializes when AccountState has studentIds
 * const students = myStudentsState.data();
 * const count = myStudentsState.count();
 *
 * // Access specific student
 * const student = myStudentsState.getStudentById('student-123');
 *
 * // Force refresh from Firestore
 * await myStudentsState.forceRefresh();
 *
 * // State automatically clears when studentIds is empty
 */

import { Injectable, inject, computed, signal, effect, untracked, Injector } from '@angular/core';
import { Unsubscribe } from '@angular/fire/firestore';
import { FirestoreService } from 'src/app/core/services/firestore/firestore';
import { Student, StudentNote } from 'src/app/core/schema';
import { AccountState } from 'src/app/store/account';
import { MyObservationsState } from 'src/app/store/my-observations';
import { MyGoalsState } from 'src/app/store/my-goals';
import { SkillsState } from 'src/app/store/skills/skills.state';
import {
  augmentStudent,
  buildActiveGoalSkillsByStudent,
  buildGoalContextMaps,
  buildMeasurementMaps,
  toFirestoreStudent,
  type AugmentationCtx,
} from 'src/app/domain/student/services/student-augmentation';
import type { AugmentedStudent } from 'src/app/domain/student/types/Student.model';
import { buildStudentNameMap } from 'src/app/domain/analytics';
import {
  MyStudentsInitConfig,
  MY_STUDENTS_DEFAULTS,
} from './my-students.types';

@Injectable({
  providedIn: 'root',
})
export class MyStudentsState {
  // =============================================
  // DEPENDENCIES
  // =============================================
  private readonly firestoreService = inject(FirestoreService);
  private readonly accountState = inject(AccountState);
  private readonly myObservationsState = inject(MyObservationsState);
  private readonly skillsState = inject(SkillsState);
  // MyGoalsState injects MyStudentsState for student name maps, so injecting
  // MyGoalsState eagerly here would create a circular dependency. Instead we
  // resolve it lazily via Injector — it is only accessed after both services
  // have been fully constructed (i.e. when the effect first runs).
  private readonly injector = inject(Injector);
  private get myGoalsState() {
    return this.injector.get(MyGoalsState);
  }

  // =============================================
  // PRIVATE STATE (Writable Signals)
  // =============================================
  private readonly _data = signal<AugmentedStudent[]>(MY_STUDENTS_DEFAULTS.data);
  private readonly _initialized = signal<boolean>(MY_STUDENTS_DEFAULTS.initialized);
  private readonly _loading = signal<boolean>(MY_STUDENTS_DEFAULTS.loading);
  private readonly _error = signal<string | null>(MY_STUDENTS_DEFAULTS.error);
  private readonly _lastUpdated = signal<number | null>(MY_STUDENTS_DEFAULTS.lastUpdated);
  private readonly _activeTeamId = signal<string | null>(MY_STUDENTS_DEFAULTS.activeTeamId);
  private readonly _studentIds = signal<string[]>(MY_STUDENTS_DEFAULTS.studentIds);

  // =============================================
  // PUBLIC STATE (Readonly Signals)
  // =============================================

  readonly data = this._data.asReadonly();
  readonly initialized = this._initialized.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly lastUpdated = this._lastUpdated.asReadonly();
  readonly activeTeamId = this._activeTeamId.asReadonly();
  readonly studentIds = this._studentIds.asReadonly();

  // =============================================
  // COMPUTED STATE (Derived Values)
  // =============================================

  readonly isReady = computed(
    () => this.initialized() && !this.loading() && !this.error()
  );

  readonly hasData = computed(() => this.data().length > 0);

  readonly count = computed(() => this.data().length);

  /**
   * `studentId → display name` lookup, memoized at the store level so
   * every widget that needs name resolution shares one Map per state
   * change instead of rebuilding it inside their own `computed()`.
   * Pairs with `resolveStudentName(map, id)` for the canonical
   * unknown-student fallback.
   */
  readonly nameMap = computed(() => buildStudentNameMap(this.data()));

  /** Get students sorted by first name, last name */
  readonly studentsSorted = computed(() => {
    const students = this.data();
    return [...students].sort((a, b) => {
      const aFirst = a.firstName?.toLowerCase() || '';
      const bFirst = b.firstName?.toLowerCase() || '';
      const aLast = a.lastName?.toLowerCase() || '';
      const bLast = b.lastName?.toLowerCase() || '';

      if (aFirst !== bFirst) return aFirst.localeCompare(bFirst);
      return aLast.localeCompare(bLast);
    });
  });

  /** Get English learner students */
  readonly englishLearners = computed(() => {
    return this.data().filter(s => s.englishLearner === true);
  });

  /** 
   * Get students by school
   * Returns array for use in computed signals in components
   */
  getStudentsBySchool(school: string): AugmentedStudent[] {
    return this.data().filter(s => s.school === school);
  }

  /** 
   * Get students by gender
   * Returns array for use in computed signals in components
   */
  getStudentsByGender(gender: 'Male' | 'Female' | 'Nonbinary'): AugmentedStudent[] {
    return this.data().filter(s => s.gender === gender);
  }

  // =============================================
  // PRIVATE SUBSCRIPTIONS
  // =============================================
  private _realtimeUnsubscribes: Unsubscribe[] = [];
  private _isInitializing = false;
  private _effectDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private _activeProfileId: string | null = null;

  // Per-batch snapshot store — the latest full collection snapshot from each
  // Firestore listener batch. Merging all slots gives the complete student list.
  // Because each slot is a FULL snapshot, a deleted document is simply absent
  // from its slot and therefore absent from the merged result.
  private _batchSnapshots = new Map<number, Student[]>();

  // =============================================
  // CONSTRUCTOR - AUTO-REACTIVE INITIALIZATION
  // =============================================

  constructor() {

    // Watch for profile, studentIds, and team changes - auto-reinitialize
    effect(() => {
      const studentIds = this.accountState.studentIds();
      const activeTeam = this.accountState.activeTeam();
      const activeTeamId = activeTeam?.id || null;
      const activeProfileId = this.accountState.activeProfileId();
      const profilesLoaded = this.accountState.hasProfiles();

      // Track previous values
      const prevStudentIds = this._studentIds();
      const prevTeamId = this._activeTeamId();
      const prevProfileId = this._activeProfileId || null;

      // Check if values actually changed (avoid unnecessary reloads)
      const studentIdsChanged =
        JSON.stringify([...prevStudentIds].sort()) !==
        JSON.stringify([...studentIds].sort());
      const teamChanged = prevTeamId !== activeTeamId;
      const profileChanged = prevProfileId !== activeProfileId;

      // Case 1: Profiles not loaded yet - wait
      if (!profilesLoaded) {
        return;
      }

      // Case 2: No team - clear state
      if (!activeTeamId) {
        if (this._initialized()) {
          this.reset();
        }
        return;
      }

      // Case 3: studentIds is empty - clear state
      if (!studentIds || studentIds.length === 0) {
        if (this._initialized()) {
          this.reset();
        }
        return;
      }

      // Case 4: Team changed - reset and reinitialize (immediate, no debounce)
      if (teamChanged && this._initialized()) {
        this.reset();
        this._activeProfileId = activeProfileId;
        this.initialize({ activeTeamId, studentIds }).catch(console.error);
        return;
      }

      // Case 5: Profile changed (within same team) - reset and reinitialize (immediate, no debounce)
      // This ensures we load the correct cache for the new profile
      if (profileChanged && this._initialized() && activeTeamId) {
        this.reset();
        this._activeProfileId = activeProfileId;
        this.initialize({ activeTeamId, studentIds }).catch(console.error);
        return;
      }

      // Update profile ID tracking
      if (!this._activeProfileId) {
        this._activeProfileId = activeProfileId;
      }

      // Clear any pending debounce timer
      if (this._effectDebounceTimer) {
        clearTimeout(this._effectDebounceTimer);
      }

      // Cases 6 & 7: Debounce studentIds changes and initial load
      this._effectDebounceTimer = setTimeout(() => {
        // Case 6: studentIds changed (same team, same profile) - reload
        if (studentIdsChanged && this._initialized() && !this._isInitializing) {
          this.initialize({ activeTeamId, studentIds }).catch(console.error);
          return;
        }

        // Case 7: Initial load
        if (!this._initialized() && !this._isInitializing && activeTeamId && studentIds.length > 0) {
          this.initialize({ activeTeamId, studentIds }).catch(console.error);
        }
      }, 200);
    });

    // Re-augment all students whenever measurements or goals change so that
    // local-only fields stay in sync. _data is read inside untracked() so
    // writing back to it does not re-trigger this effect.
    effect(() => {
      const measurements = this.myObservationsState.data();
      const goals = this.myGoalsState.data();
      untracked(() => {
        const students = this._data();
        if (students.length === 0) return;
        this._data.set(this.augmentAll(students));
      });
    });
  }

  // =============================================
  // LIFECYCLE & INITIALIZATION
  // =============================================

  async initialize(config: MyStudentsInitConfig): Promise<void> {
    if (this._isInitializing) {
      console.warn('[MyStudentsState] Initialization already in progress');
      return;
    }

    this._isInitializing = true;
    this._loading.set(true);
    this._error.set(null);
    this._activeTeamId.set(config.activeTeamId);
    this._studentIds.set(config.studentIds);


    try {
      // Load from active team + any additional teams in parallel
      const [activeStudents, ...additionalStudentBatches] = await Promise.all([
        this.loadStudents(config.activeTeamId, config.studentIds),
        ...(config.additionalTeamContexts ?? []).map(ctx =>
          this.loadStudents(ctx.teamId, ctx.studentIds)
        ),
      ]);

      const allStudents = [activeStudents, ...additionalStudentBatches].flat();

      this._data.set(this.augmentAll(allStudents));

      // Update metadata
      this._initialized.set(true);
      this._lastUpdated.set(Date.now());

      // Start real-time listener for the active team only
      if (config.enableRealtimeUpdates !== false) {
        this.startRealtimeListener(config.activeTeamId, config.studentIds);
      }

    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize';
      this._error.set(errorMessage);
      console.error('[MyStudentsState] ❌ Initialization failed:', error);
      throw error;
    } finally {
      this._loading.set(false);
      this._isInitializing = false;
    }
  }

  reset(): void {
    this.stopRealtimeListener();

    // Clear any pending debounce timer
    if (this._effectDebounceTimer) {
      clearTimeout(this._effectDebounceTimer);
      this._effectDebounceTimer = null;
    }

    this._batchSnapshots.clear();

    this._data.set(MY_STUDENTS_DEFAULTS.data);
    this._initialized.set(MY_STUDENTS_DEFAULTS.initialized);
    this._loading.set(MY_STUDENTS_DEFAULTS.loading);
    this._error.set(MY_STUDENTS_DEFAULTS.error);
    this._lastUpdated.set(MY_STUDENTS_DEFAULTS.lastUpdated);
    this._activeTeamId.set(MY_STUDENTS_DEFAULTS.activeTeamId);
    this._studentIds.set(MY_STUDENTS_DEFAULTS.studentIds);
    this._isInitializing = false;
    this._activeProfileId = null;

  }

  // =============================================
  // PUBLIC METHODS
  // =============================================

  /**
   * Force refresh from Firestore
   */
  async forceRefresh(): Promise<void> {
    const teamId = this._activeTeamId();
    const studentIds = this._studentIds();

    if (!teamId) {
      console.warn('[MyStudentsState] Cannot force refresh - no active team');
      return;
    }

    if (!studentIds || studentIds.length === 0) {
      console.warn('[MyStudentsState] Cannot force refresh - no studentIds');
      return;
    }


    // Reinitialize
    await this.initialize({
      activeTeamId: teamId,
      studentIds,
      enableRealtimeUpdates: true,
    });
  }

  /**
   * Refresh data (alias for forceRefresh)
   */
  async refresh(): Promise<void> {
    return this.forceRefresh();
  }

  /**
   * Get a specific student by ID
   */
  getStudentById(id: string): AugmentedStudent | null {
    return this.data().find(s => this.getStudentId(s) === id) ?? null;
  }

  /**
   * Remove a student from local state immediately after a successful Firestore delete.
   * The real-time listener will also fire shortly after, but this keeps the UI
   * responsive without waiting for the round-trip.
   */
  removeStudent(id: string): void {
    this._data.update(students => students.filter(s => this.getStudentId(s) !== id));
    // Cascade to measurements immediately. Goals and observations listen reactively
    // to this.data() via their own effects, so they self-clean when _data changes above.
    // MyObservationsState cannot inject MyStudentsState (circular dep), so we push here.
    this.myObservationsState.removeByStudentId(id);
  }

  /**
   * Optimistically patch a student in local state immediately (before Firestore round-trip).
   * Call this after writing to Firestore so the UI reflects the change without waiting
   * for the real-time listener to fire.
   */
  patchStudent(id: string, updates: Partial<Student>): void {
    this._data.update(students =>
      students.map(s => (this.getStudentId(s) === id ? { ...s, ...updates } : s))
    );
  }

  /**
   * Add a note to a student.
   *
   * Optimistic: the local state is patched synchronously so the caller can
   * immediately reflect the new note in the UI (close a modal, repaint the
   * list, etc.). The Firestore write fires in the background; failures are
   * logged with the student/note IDs but do not roll back the local change
   * — pre-V1 we'd rather keep the snappy UX and surface divergence on the
   * next reconciliation than yank the note out from under the user.
   *
   * Returns the constructed note synchronously. Callers no longer need to
   * `await` this method.
   */
  addNote(studentId: string, content: string): StudentNote {
    const activeTeamId = this.accountState.activeTeam()?.id;
    if (!activeTeamId) throw new Error('No active team');

    const currentUser = this.accountState.email() || 'unknown';
    const now = Date.now();

    const note: StudentNote = {
      id: crypto.randomUUID(),
      content,
      createdAt: now,
      createdBy: currentUser,
      updatedAt: now,
      updatedBy: currentUser,
    };

    const student = this.getStudentById(studentId);
    const updatedNotes = [...(student?.notes ?? []), note];

    // Patch local state first — the UI repaints from this signal.
    this.patchStudent(studentId, { notes: updatedNotes });

    // Fire the Firestore write in the background. Logged on failure but the
    // local note stays put.
    void this.firestoreService
      .updateDocumentAsync(`partners/${activeTeamId}/students/${studentId}`, { notes: updatedNotes })
      .catch(err => {
        console.error('[MyStudents] Note write failed:', { studentId, noteId: note.id, err });
      });

    return note;
  }

  /**
   * Edit an existing note's content. Same optimistic pattern as `addNote` —
   * patches local state synchronously, fires the Firestore write in the
   * background, logs failures without rolling back the local change.
   *
   * No-op if the student or note id can't be found locally.
   */
  editNote(studentId: string, noteId: string, content: string): void {
    const activeTeamId = this.accountState.activeTeam()?.id;
    if (!activeTeamId) throw new Error('No active team');

    const student = this.getStudentById(studentId);
    const existing = student?.notes ?? [];
    if (!existing.some(n => n.id === noteId)) return;

    const currentUser = this.accountState.email() || 'unknown';
    const now = Date.now();

    const updatedNotes = existing.map(n =>
      n.id === noteId ? { ...n, content, updatedAt: now, updatedBy: currentUser } : n,
    );

    this.patchStudent(studentId, { notes: updatedNotes });

    void this.firestoreService
      .updateDocumentAsync(`partners/${activeTeamId}/students/${studentId}`, { notes: updatedNotes })
      .catch(err => {
        console.error('[MyStudents] Note edit write failed:', { studentId, noteId, err });
      });
  }

  /**
   * Remove a note from a student. Same optimistic pattern as `addNote`.
   * No-op if the student or note id can't be found locally.
   */
  removeNote(studentId: string, noteId: string): void {
    const activeTeamId = this.accountState.activeTeam()?.id;
    if (!activeTeamId) throw new Error('No active team');

    const student = this.getStudentById(studentId);
    const existing = student?.notes ?? [];
    if (!existing.some(n => n.id === noteId)) return;

    const updatedNotes = existing.filter(n => n.id !== noteId);

    this.patchStudent(studentId, { notes: updatedNotes });

    void this.firestoreService
      .updateDocumentAsync(`partners/${activeTeamId}/students/${studentId}`, { notes: updatedNotes })
      .catch(err => {
        console.error('[MyStudents] Note delete write failed:', { studentId, noteId, err });
      });
  }

  /**
   * Replace a student's accommodations list. Optimistic — patches local
   * state synchronously and fires the Firestore write in the background.
   * Same failure semantics as the note methods.
   */
  updateAccommodations(studentId: string, accommodations: string[]): void {
    const activeTeamId = this.accountState.activeTeam()?.id;
    if (!activeTeamId) throw new Error('No active team');

    this.patchStudent(studentId, { accommodations });

    void this.firestoreService
      .updateDocumentAsync(`partners/${activeTeamId}/students/${studentId}`, { accommodations })
      .catch(err => {
        console.error('[MyStudents] Accommodations write failed:', { studentId, err });
      });
  }

  /**
   * Generic optimistic patch for a student record. Accepts any subset of
   * `Student` fields, applies them locally first, then fires the Firestore
   * write in the background. Same failure semantics as the note methods.
   *
   * Use this for inline single-field edits or any other partial update —
   * it's the canonical "I changed N fields, save them" entry point.
   */
  updateStudentFields(studentId: string, fields: Partial<Student>): void {
    const activeTeamId = this.accountState.activeTeam()?.id;
    if (!activeTeamId) throw new Error('No active team');
    if (Object.keys(fields).length === 0) return;

    this.patchStudent(studentId, fields);

    void this.firestoreService
      .updateDocumentAsync(`partners/${activeTeamId}/students/${studentId}`, fields)
      .catch(err => {
        console.error('[MyStudents] Student field write failed:', { studentId, fields, err });
      });
  }

  /**
   * Replace a student's modifications list. Same optimistic pattern as
   * `updateAccommodations`.
   */
  updateModifications(studentId: string, modifications: string[]): void {
    const activeTeamId = this.accountState.activeTeam()?.id;
    if (!activeTeamId) throw new Error('No active team');

    this.patchStudent(studentId, { modifications });

    void this.firestoreService
      .updateDocumentAsync(`partners/${activeTeamId}/students/${studentId}`, { modifications })
      .catch(err => {
        console.error('[MyStudents] Modifications write failed:', { studentId, err });
      });
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this._error.set(null);
  }


  // =============================================
  // PRIVATE METHODS (Helpers)
  // =============================================

  /**
   * Get student ID from Student object
   * Handles both id and studentId fields
   */
  private getStudentId(student: Student): string {
    return student.id || '';
  }

  /** Builds the full augmentation context in one place. */
  private buildAugmentationCtx(): AugmentationCtx {
    const skills = this.skillsState.skills();
    const goals = this.myGoalsState.data();

    // Single O(M) pass over measurements produces all measurement-derived maps.
    const { measuredSkillsByStudent, latestMeasurementsByStudent, skillSummaryByStudent } =
      buildMeasurementMaps(this.myObservationsState.data());

    // Single O(G) pass over goals produces skill sets and scheduling.
    const { iepGoalSkillsByStudent, schedulingByStudent } =
      buildGoalContextMaps(goals, skillSummaryByStudent);

    return {
      measuredSkillsByStudent,
      latestMeasurementsByStudent,
      skillSummaryByStudent,
      activeGoalSkillsByStudent: buildActiveGoalSkillsByStudent(goals),
      iepGoalSkillsByStudent,
      schedulingByStudent,
      skillNameMap: new Map(skills.map(s => [s.id, s.name])),
      skillAreaBySkillId: new Map(skills.map(s => [s.id, s.area])),
    };
  }

  /** Augments an array of raw students using a freshly built context. */
  private augmentAll(students: Student[]): AugmentedStudent[] {
    const ctx = this.buildAugmentationCtx();
    return students.map(s => augmentStudent(s, ctx));
  }

  /**
   * Split array into chunks of specified size
   */
  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  // =============================================
  // PRIVATE METHODS (Data Loading)
  // =============================================

  /**
   * Load students from Firestore
   * 
   * Loads all students whose IDs are in the studentIds array.
   */
  private async loadStudents(
    activeTeamId: string,
    studentIds: string[]
  ): Promise<Student[]> {
    if (studentIds.length === 0) {
      return [];
    }

    const collectionPath = `partners/${activeTeamId}/students`;


    try {
      // Handle batching for large studentIds arrays (Firestore 'in' limit is 10)
      const allStudents: Student[] = [];
      const batches = this.chunkArray(studentIds, 10);

      for (const batch of batches) {

        const batchStudents = await this.firestoreService.getCollectionAsync<Student>(
          collectionPath,
          {
            where: [
              { field: 'id', operator: 'in', value: batch }
            ],
          }
        );


        allStudents.push(...batchStudents);
      }


      return allStudents;
    } catch (error) {
      console.error('[MyStudentsState] Failed to load students:', error);
      throw error;
    }
  }

  /**
   * Merge new/updated students into existing data
   */
  private mergeStudents(newStudents: Student[]): void {
    const currentData = this._data();
    const studentIds = this._studentIds();

    const ctx = this.buildAugmentationCtx();

    // Create map of current students
    const dataMap = new Map(
      currentData.map(s => [this.getStudentId(s), s])
    );

    // Augment and update or add new students
    newStudents.forEach(student => {
      const id = this.getStudentId(student);
      if (id) dataMap.set(id, augmentStudent(student, ctx));
    });

    // Filter to only students in current studentIds
    const filtered = Array.from(dataMap.values()).filter(s => {
      const id = this.getStudentId(s);
      return id && studentIds.includes(id);
    });

    this._data.set(filtered);

    // Update metadata
    this._lastUpdated.set(Date.now());

  }

  /**
   * Start real-time Firestore listeners for the student documents in this caseload.
   *
   * Firestore's `in` operator is capped at 10 values, so we create one listener
   * per batch of 10 student IDs. Each listener stores its own latest full snapshot
   * in `_batchSnapshots`. Whenever any batch fires, all snapshots are merged and
   * `_data` is replaced entirely — so deletions in Firestore are reflected
   * immediately without any additional logic.
   */
  private startRealtimeListener(activeTeamId: string, studentIds: string[]): void {
    if (studentIds.length === 0) {
      console.warn('[MyStudentsState] Cannot start listener — empty studentIds');
      return;
    }

    this.stopRealtimeListener();
    this._batchSnapshots.clear();

    const collectionPath = `partners/${activeTeamId}/students`;
    const batches = this.chunkArray(studentIds, 10);
    const totalBatches = batches.length;


    batches.forEach((batch, batchIndex) => {
      // Pre-seed each slot so the merge doesn't skip uninitialised batches
      this._batchSnapshots.set(batchIndex, []);

      const unsubscribe = this.firestoreService.onCollectionChange<Student>(
        collectionPath,
        (batchStudents: Student[]) => {
          // Replace this batch's snapshot with the latest from Firestore.
          // Deleted documents will simply be absent from batchStudents.
          this._batchSnapshots.set(batchIndex, batchStudents);

          // Merge every batch's latest snapshot into one flat array.
          const allStudents: Student[] = [];
          for (let i = 0; i < totalBatches; i++) {
            allStudents.push(...(this._batchSnapshots.get(i) ?? []));
          }

          const ctx = this.buildAugmentationCtx();
          this._data.set(allStudents.map(s => augmentStudent(s, ctx)));
          this._lastUpdated.set(Date.now());

        },
        { where: [{ field: 'id', operator: 'in', value: batch }] }
      );

      this._realtimeUnsubscribes.push(unsubscribe);
    });

  }

  /**
   * Stop all real-time listeners and clear snapshot cache
   */
  private stopRealtimeListener(): void {
    if (this._realtimeUnsubscribes.length > 0) {
      this._realtimeUnsubscribes.forEach(unsubscribe => unsubscribe());
      this._realtimeUnsubscribes = [];
    }
    this._batchSnapshots.clear();
  }
}
